import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getGiftCode,
  getGiftCodeRedemptions,
  calculateGiftCodeStatus,
} from "@/services/firebase/giftCode";
import { GiftCodeStatus } from "@/types/giftCode";
import { format } from "date-fns";
import { zhTW } from "date-fns/locale";
import {
  AdminPage,
  AdminPageHeader,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminDataTable,
  AdminEmptyState,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, Pencil } from "lucide-react";

const statusConfig: Record<GiftCodeStatus, { label: string; variant: "default" | "destructive" | "secondary" | "outline" }> = {
  active: { label: "啟用中", variant: "default" },
  expired: { label: "已過期", variant: "destructive" },
  depleted: { label: "已用完", variant: "secondary" },
};

const redemptionColumns: AdminTableColumn[] = [
  { key: "user_id", label: "用戶 ID" },
  { key: "coins", label: "獲得金幣", sortable: true },
  { key: "redeemed_at", label: "兌換時間", sortable: true },
];

export default function GiftCodeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [redemptionLimit, setRedemptionLimit] = useState(50);

  const { data: giftCode, isLoading, error } = useQuery({
    queryKey: ["gift-code", id],
    queryFn: () => getGiftCode(id!),
    enabled: !!id,
  });

  const { data: redemptions, isLoading: redemptionsLoading } = useQuery({
    queryKey: ["gift-code-redemptions", id, redemptionLimit],
    queryFn: () => getGiftCodeRedemptions(giftCode?.code || "", redemptionLimit),
    enabled: !!giftCode?.code,
  });

  if (isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  if (error || !giftCode) {
    return (
      <AdminPage>
        <AdminErrorAlert
          message={error instanceof Error ? error.message : "找不到此禮物代碼"}
        />
        <Button variant="outline" onClick={() => navigate("/gift-codes")} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          返回列表
        </Button>
      </AdminPage>
    );
  }

  const currentStatus = calculateGiftCodeStatus(giftCode);
  const statusInfo = statusConfig[currentStatus];
  const usagePercentage = giftCode.total_uses > 0
    ? Math.round((giftCode.used_count / giftCode.total_uses) * 100)
    : 0;
  const remainingUses = giftCode.total_uses - giftCode.used_count;
  const totalCoinsDistributed = (redemptions || []).reduce((sum, r) => sum + r.coins, 0);

  return (
    <AdminPage>
      <AdminPageHeader
        title="禮物代碼詳情"
        subtitle={`代碼：${giftCode.code}`}
        actions={
          <div className="flex gap-2">
            <Button variant="default" onClick={() => navigate(`/gift-codes/${id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              編輯
            </Button>
            <Button variant="outline" onClick={() => navigate("/gift-codes")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              返回列表
            </Button>
          </div>
        }
      />

      {/* 基本資訊 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>基本資訊</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">代碼</Label>
              <p className="text-lg font-semibold text-foreground mt-1">
                {giftCode.code}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">狀態</Label>
              <div className="mt-1">
                <Badge variant={statusInfo.variant}>
                  {statusInfo.label}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">金幣數量</Label>
              <p className="text-lg font-semibold text-foreground mt-1">
                {giftCode.coins.toLocaleString()} 金幣
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">每人限用次數</Label>
              <p className="text-lg font-semibold text-foreground mt-1">
                {giftCode.usage_limit_per_user} 次
              </p>
            </div>
            {giftCode.description && (
              <div className="md:col-span-2">
                <Label className="text-muted-foreground">描述</Label>
                <p className="text-foreground mt-1">{giftCode.description}</p>
              </div>
            )}
            <div>
              <Label className="text-muted-foreground">生效時間</Label>
              <p className="text-foreground mt-1">
                {giftCode.valid_from
                  ? format(new Date(giftCode.valid_from), "yyyy年MM月dd日 HH:mm", { locale: zhTW })
                  : "-"}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">失效時間</Label>
              <p className="text-foreground mt-1">
                {giftCode.valid_until
                  ? format(new Date(giftCode.valid_until), "yyyy年MM月dd日 HH:mm", { locale: zhTW })
                  : "-"}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">建立時間</Label>
              <p className="text-foreground mt-1">
                {giftCode.created_at
                  ? format(new Date(giftCode.created_at), "yyyy年MM月dd日 HH:mm", { locale: zhTW })
                  : "-"}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">建立者</Label>
              <p className="text-foreground mt-1">
                {giftCode.created_by || "-"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 統計資訊 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>統計資訊</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label className="text-muted-foreground">使用次數</Label>
              <p className="text-2xl font-bold text-foreground mt-2">
                {giftCode.used_count} / {giftCode.total_uses}
              </p>
              <div className="w-full bg-muted rounded-full h-3 mt-3">
                <div
                  className={`h-3 rounded-full ${
                    usagePercentage >= 100
                      ? "bg-danger"
                      : usagePercentage >= 80
                      ? "bg-warning"
                      : "bg-success"
                  }`}
                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                使用率：{usagePercentage}%
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">剩餘次數</Label>
              <p className="text-2xl font-bold text-foreground mt-2">
                {remainingUses}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                還可被兌換 {remainingUses} 次
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">已發放金幣總數</Label>
              <p className="text-2xl font-bold text-foreground mt-2">
                {totalCoinsDistributed.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                共 {redemptions?.length || 0} 筆兌換記錄
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 兌換記錄 */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>兌換記錄</CardTitle>
          <div className="flex items-center gap-2">
            <Label className="text-muted-foreground text-sm">顯示數量：</Label>
            <Select
              value={String(redemptionLimit)}
              onValueChange={(val) => setRedemptionLimit(Number(val))}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="200">200</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <AdminDataTable
            columns={redemptionColumns}
            data={redemptions || []}
            loading={redemptionsLoading}
            emptyTitle="尚無兌換記錄"
            getRowId={(item) => item.id}
            renderCell={(redemption, col) => {
              switch (col.key) {
                case "user_id":
                  return (
                    <span className="text-sm font-mono text-foreground">
                      {redemption.user_id}
                    </span>
                  );
                case "coins":
                  return (
                    <span className="text-sm font-semibold text-foreground">
                      {redemption.coins.toLocaleString()} 金幣
                    </span>
                  );
                case "redeemed_at":
                  return (
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(redemption.redeemed_at), "yyyy年MM月dd日 HH:mm:ss", {
                        locale: zhTW,
                      })}
                    </span>
                  );
                default:
                  return null;
              }
            }}
          />
        </CardContent>
      </Card>
    </AdminPage>
  );
}
